import type { ReactElement } from "react"
import { useEffect } from "react"
import React from "react"
import _t from "@core/i18n"
import * as Sentry from "@sentry/browser"
import { graphql } from "gatsby"
import { get, isEqual } from "lodash"
import BreakpointVisibilityOrg from "@onestore/hel/dist/components/organisms/BreakpointVisibilityOrg"
import ContainerOrg from "@onestore/hel/dist/components/organisms/ContainerOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import ToggleSectionContainerOrg from "@onestore/hel/dist/components/organisms/ToggleSectionContainerOrg"
import ContactSection from "@gatsby-plugin-generic-page/sections/ContactSection"
import DescriptionBoxSection from "@gatsby-plugin-generic-page/sections/DescriptionBoxSection"
import MarketProductParameters from "@gatsby-plugin-marketplace/components/MarketProductParameters"
import ProductBundle from "@gatsby-plugin-marketplace/components/ProductBundle"
import ProductCostCalculator from "@gatsby-plugin-marketplace/components/ProductCostCalculator"
import ProductDescription from "@gatsby-plugin-marketplace/components/ProductDescription"
import ProductFeatures from "@gatsby-plugin-marketplace/components/ProductFeatures"
import ProductHeader from "@gatsby-plugin-marketplace/components/ProductHeader"
import ProductSeo from "@gatsby-plugin-marketplace/components/ProductSeo"
import ProductSlider from "@gatsby-plugin-marketplace/components/ProductsSlider"
import LastViewedProductsContainer from "@gatsby-plugin-marketplace/containers/LastViewedProductsContainer"
import type { MarketplaceProduct } from "@gatsby-plugin-marketplace/fragments/marketplaceProduct"
import type { ProductCardItem } from "@gatsby-plugin-marketplace/fragments/marketplaceProductCard"
import SquidexA11yToolbar from "@gatsby-plugin-squidex/components/SquidexA11yToolbar"
import ExternalScripts from "~/components/ExternalScripts"
import FaqContent from "~/components/FaqContent"
import Metadata from "~/components/Metadata"
import ProductSchema from "~/components/ProductSchema"
import type { ComparableProduct } from "~/fragments/comparableProduct"
import type { SaleConfiguration } from "~/fragments/saleConfigurations"
import isEmpty from "~/lib/isEmpty"
import { COMPONENT_ID } from "~/types/component"
import type { ProductBreadcrumbs } from "~/types/Product"
import MarketFeatureSlider from "../../components/MarketFeatureSlider"

type ProductPageProps = {
  data: {
    GraphCmsSquidex: {
      product: MarketplaceProduct
      similarProducts: ProductCardItem[]
    }
  }
}

type ParametersSectionProps = {
  title: string
  saleConfiguration?: SaleConfiguration
  products: ComparableProduct[]
}

function ParametersSection({
  title,
  saleConfiguration,
  products,
}: ParametersSectionProps): ReactElement<ParametersSectionProps> {
  return (
    <ToggleSectionContainerOrg
      id={COMPONENT_ID.MARKETPLACE_COMPARE}
      hideHeadingOnBiggerScreen
      isMobileSectionSticky
      heading={title}
    >
      <MarketProductParameters
        saleConfiguration={saleConfiguration}
        products={products}
      />
    </ToggleSectionContainerOrg>
  )
}

export default function ProductPage({
  data: {
    GraphCmsSquidex: { product, similarProducts },
  },
}: ProductPageProps): ReactElement<ProductPageProps> | null {
  const {
    flatData: {
      saleConfigurations,
      bundles,
      primaryCategory,
      comparableProducts,
      name,
      benefits,
      headerBgSmall,
      headerBgMedium,
      headerBgLarge,
      headerLogo,
      headerLogoMedium,
      headerBadge,
      details,
      badges,
      tableOnTop,
      features,
      detailedTitle,
      detailedDescription,
      featureSlider,
      seoText,
      faq,
      recommendedProducts,
      contactSection,
      smallBanner,
      infoBox,
    },
  } = product

  useEffect(() => {
    const hasPlan = !!get(
      product,
      "flatData.saleConfigurations[0].flatData.defaultCloudBluePlan[0].id"
    )

    if (!hasPlan) {
      Sentry.captureMessage(
        `Product with name: "${name}" and ID: "${product.id}" doesn't have active AX config or plan`,
        "error"
      )
    }
  }, [])

  const page = product.flatData.pages[0]

  const productBreadcrumbs: ProductBreadcrumbs = {
    current: name,
    items: [
      {
        name: _t("marketplace.name"),
        src: "/marketplace/",
      },
      {
        name: primaryCategory[0].flatData.name,
        src: primaryCategory[0].flatData.pages[0].flatData.url,
      },
    ],
  }

  const productHeader = {
    name,
    details,
    badges: badges || [],
    headerBgSmall: headerBgSmall[0],
    headerBgMedium: headerBgMedium[0],
    headerBgLarge: headerBgLarge[0],
    headerLogo: headerLogo[0],
    headerLogoMedium: headerLogoMedium && headerLogoMedium[0],
    headerBadge,
  }

  const parametersTitle =
    comparableProducts &&
    comparableProducts.length === 1 &&
    isEqual(
      saleConfigurations[0].id,
      get(comparableProducts[0], "saleConfiguration.id")
    )
      ? _t("marketplace.productSpecification")
      : _t("marketplace.compareProducts")

  const featuresJsx = !tableOnTop && !isEmpty(features) && (
    <ProductFeatures productFeatures={features} />
  )

  const bundleJsx = !isEmpty(bundles) && (
    <ProductBundle product={product} bundles={bundles} />
  )

  const descriptionJsx = detailedDescription && (
    <ProductDescription productDescription={detailedDescription} />
  )

  return (
    <>
      <ExternalScripts page={page} />

      <Metadata metadata={page} noIndex={page.flatData.noIndex} />

      <ProductSchema
        url={page.flatData.url}
        items={page.flatData.productSchemas || []}
      />

      <ProductHeader
        saleConfiguration={saleConfigurations[0]}
        productBreadcrumbs={productBreadcrumbs}
        productHeader={productHeader}
        features={featuresJsx}
        bundle={bundleJsx}
        detailedTitle={detailedTitle}
        description={descriptionJsx}
        hasCompareProducts={!isEmpty(comparableProducts)}
        benefits={benefits}
        banner={smallBanner}
        badge={headerBadge}
      />

      {tableOnTop && !isEmpty(comparableProducts) ? (
        <ParametersSection
          saleConfiguration={saleConfigurations[0]}
          products={comparableProducts}
          title={parametersTitle}
        />
      ) : null}

      {!isEmpty(featuresJsx) ? (
        <BreakpointVisibilityOrg from="medium" to="large">
          <ContainerOrg>{featuresJsx}</ContainerOrg>
        </BreakpointVisibilityOrg>
      ) : null}

      {!isEmpty(bundleJsx) ? (
        <BreakpointVisibilityOrg from="small" to="large">
          <ToggleSectionContainerOrg
            id={COMPONENT_ID.PRODUCT_BUNDLE}
            color="light"
            heading={_t("marketplace.bundleBuy")}
            spacing="small"
          >
            {bundleJsx}
          </ToggleSectionContainerOrg>
        </BreakpointVisibilityOrg>
      ) : null}

      {!isEmpty(saleConfigurations) &&
      !isEmpty(saleConfigurations[0].flatData.costCalculator) &&
      !isEmpty(saleConfigurations[0].flatData.defaultCloudBluePlan) &&
      !isEmpty(
        saleConfigurations[0].flatData.defaultCloudBluePlan[0].flatData
      ) ? (
        <BreakpointVisibilityOrg from="small" to="large">
          <ToggleSectionContainerOrg
            id={COMPONENT_ID.COST_CALCULATOR}
            color="light"
            heading={_t("marketplace.costCalculator")}
            spacing="small"
          >
            <ProductCostCalculator
              saleConfiguration={saleConfigurations[0]}
              defaultCloudBluePlanData={
                saleConfigurations[0].flatData.defaultCloudBluePlan[0].flatData
              }
              calculatorData={saleConfigurations[0].flatData.costCalculator}
            />
          </ToggleSectionContainerOrg>
        </BreakpointVisibilityOrg>
      ) : null}

      <BreakpointVisibilityOrg from="small" to="large">
        <ToggleSectionContainerOrg
          isActive={true}
          color="light"
          heading={_t("marketplace.detailedDescription")}
          spacing="small"
        >
          {descriptionJsx}
        </ToggleSectionContainerOrg>
      </BreakpointVisibilityOrg>

      {!isEmpty(featureSlider) && (
        <MarketFeatureSlider featureSlider={featureSlider} />
      )}

      {!tableOnTop && !isEmpty(comparableProducts) && (
        <ParametersSection
          saleConfiguration={saleConfigurations[0]}
          products={comparableProducts}
          title={parametersTitle}
        />
      )}

      {!isEmpty(infoBox) && <DescriptionBoxSection {...infoBox[0].flatData} />}

      {!isEmpty(recommendedProducts) && (
        <ProductSlider
          title={_t("marketplace.recommendedProducts")}
          products={recommendedProducts}
        />
      )}

      {!isEmpty(similarProducts) && (
        <ProductSlider
          title={_t("marketplace.similarProducts")}
          products={similarProducts}
        />
      )}

      <LastViewedProductsContainer currentUrl={page.flatData.url} />

      {(!isEmpty(faq) || !isEmpty(seoText)) && (
        <ToggleSectionContainerOrg
          heading={_t("marketplace.faqTitle")}
          spacing="small"
        >
          {!isEmpty(faq) && <FaqContent hasFaqSchema faqs={faq} />}

          {!isEmpty(seoText) && (
            <PushOrg topSpace={5}>
              <ProductSeo productSeoText={seoText} />
            </PushOrg>
          )}
        </ToggleSectionContainerOrg>
      )}

      {!isEmpty(contactSection) && (
        <ContactSection {...contactSection[0].flatData} />
      )}

      <SquidexA11yToolbar page={product} />
    </>
  )
}

export const query = graphql`
  query ($productId: String!, $similarProductsFilter: String!) {
    GraphCmsSquidex {
      similarProducts: queryMarketplaceProductContents(
        filter: $similarProductsFilter
      ) {
        ...MarketplaceProductCard
      }
      product: findMarketplaceProductContent(id: $productId) {
        ...MarketplaceProduct
      }
    }
  }
`
